import { EventWithContext } from "../api";
import { IAllGames } from "../dataProcessors";
import { PlayerID, TeamID } from "../models/common";
import { BlaseballEvent, ChroniclerItem, ElectionEvent, GameEvent, PlayerModel, TeamModel } from "../models/models";
import { chroniclerItemCoversTime, dateAdd } from "../utility";

// These search through events on chron
const searches = ["lose join", "umpire", "quest", "burp", "finds", "scuttling"];


// These are keywords to be emphasized in an event
const keywords = ["incinerates", "Alternate", "Side Quest", "Can't Lose", "[ BURP ]!",
    "Heart", "incinerated",
    "Mage Umpire", "Rogue Umpire", "Knight Umpire", "Bard Umpire",
    "Knight"] as const
export type keyword = (typeof keywords[number])

interface eventPiece {
    _type: "plain" | "keyword",
    text: string,
    class: string
}
function splitMessage(message: string, playerNames: string[] = [], teamNames: string[] = []): (eventPiece)[] {

    let current = message;
    let segments: (eventPiece)[] = [];

    const searchWords = playerNames.concat(teamNames).concat(keywords);

    function firstKeywordIndex(val: string): number {
        const foundIdxs = searchWords.map(keyword => val.indexOf(keyword)).filter(idx => idx !== -1)
        if (foundIdxs.length > 0) {
            return Math.min(...foundIdxs)
        }
        return -1;
    }

    function nextKeyword(val: string): string | undefined {
        const pairs = searchWords.map(keyword => {
            return {
                idx: val.indexOf(keyword),
                keyword
            }
        }).filter(pair => pair.idx !== -1);
        pairs.sort((a, b) => (a.idx - b.idx))

        if (pairs.length) {
            return pairs[0].keyword
        }

        return undefined
    }


    function plain(text: string): eventPiece {
        return {
            _type: "plain",
            text,
            class: ""
        }
    }

    function keywordify(keyword: string): eventPiece {
        return {
            _type: "keyword",
            text: keyword,
            class: playerNames.includes(keyword) ? "Player serif" : teamNames.includes(keyword) ? "Team serif" : keyword
        }
    }

    let going = true;
    while (going && firstKeywordIndex(current) !== -1) {
        const startIdx = firstKeywordIndex(current);
        const keyword = nextKeyword(current);
        if (keyword) {
            const len = keyword.length
            segments.push(plain(current.substr(0, startIdx)))
            segments.push(keywordify(keyword))
            current = current.substr(startIdx + len);
        } else {
            console.log("Failed to parse event")
            going = false;
        }
    }
    segments.push(plain(current))

    return segments;
}

// These are event types, to pick how to summarize the event
export type eventType = "unknown" | "incineration" | "burp-miss" | "burp-hit" | "sidequest" | "cant-lose" | "alternation" | "quest-progress" | "favorable-rule"
    | "shadow-swap" | "stat-reroll" | "yeet"
interface eventTypeSearch {
    type: eventType,
    search: string
}

const eventTypeSearches: eventTypeSearch[] = [
    {
        type: "incineration",
        search: "incinerates"
    },
    {
        type: "incineration",
        search: "is incinerated!"
    },
    {
        type: "burp-hit",
        search: "crashes into"
    },
    {
        type: "burp-miss",
        search: "crashes into the Field"
    },
    {
        type: "sidequest",
        search: "Side Quest"
    },
    {
        type: "cant-lose",
        search: "Can't Lose! They join"
    },
    {
        type: "alternation",
        search: "Alternate!" // This is a sketchy search
    },
    {
        type: "quest-progress",
        search: "finds a Heart"
    },
    {
        type: "quest-progress",
        search: "retreats from sounds of scuttling..."
    },
    {
        type: "favorable-rule",
        search: "favor!" // Also a sketchy search
    },
    {
        type: "alternation",
        search: "Alternate is called!"
    },
    {
        type: "stat-reroll",
        search: "learns to Defend!"
    },
    {
        type: "stat-reroll",
        search: "gains 3 Stars of"
    },
    {
        type: "shadow-swap",
        search: "is called up to replace"
    },
    {
        type: "yeet",
        search: "yeet"
    },
]

function isGameEvent(event: BlaseballEvent): event is GameEvent {
    return !!((event as any).game_id)
}

function isElectionEvent(event: BlaseballEvent): event is ElectionEvent {
    return ((event as any).outcomeStrings !== undefined)
}

function identifier(event: BlaseballEvent): string {
    if (isGameEvent(event)) {
        return (event.game_id as string) + "/" + (event.data.displayOrder) + ";" + (event.data.displayText)
    }
    return (event.seasonId as string) + "/" + (event.name)
}

function getEventType(event: BlaseballEvent): eventType {

    const eventBody = (isGameEvent(event)) ? event.data.displayText : event.outcomeStrings.join(";")
    let type: eventType = "unknown"
    eventTypeSearches.forEach(search => {
        if (eventBody.includes(search.search)) {
            type = search.type
        }
    })

    return type;



}

interface context {
    teamsBefore: TeamModel[],
    teamsAfter: TeamModel[],
    playersBefore: PlayerModel[],
    playersAfter: PlayerModel[]
}

function eventContext(eventTime: string, eventText: string, relevantTeamIds: TeamID[], teamVersionsById: Record<TeamID, ChroniclerItem<TeamModel>[]>,
    playerVersionsById: Record<PlayerID, ChroniclerItem<PlayerModel>[]>, extraPlayersToLoad: PlayerID[] = [], minimumTime: number = 1): context {
    if (relevantTeamIds.length === 0) {
        return {
            teamsBefore: [],
            teamsAfter: [],
            playersBefore: [],
            playersAfter: []
        }
    }

    const relevantTeamVersions = relevantTeamIds.map(id => teamVersionsById[id]).reduce((a, b) => a.concat(b), [])

    const beforeData = relevantTeamVersions.filter(item => chroniclerItemCoversTime(item, eventTime))
    const teamsBefore = beforeData.map(item => item.data)

    const afterData = beforeData.map(teamDataBefore => {
        if (teamDataBefore.valid_to === null) {
            //console.warn(`No next value available for ${teamDataBefore.data.name} at ${eventTime} [${event.data.displayText}]`)
            return teamDataBefore;
        }
        const nextTime = dateAdd(teamDataBefore.valid_to, minimumTime);
        const matches = relevantTeamVersions.filter(item => item.data.id === teamDataBefore.data.id && chroniclerItemCoversTime(item, nextTime))
        if (matches.length !== 1) {
            console.warn(`Got ${matches.length} matches for the next version of ${teamDataBefore.data.name} at ${eventTime} [${eventText}]`)
            return teamDataBefore;
        }

        return matches[0];

    })

    const teamsAfter = afterData.map(item => item.data);

    const playerIdsOnTeams = teamsBefore.map(team => team.roster.map(player => player.id)).reduce((a, b) => a.concat(b))

    let playersBeforeData: ChroniclerItem<PlayerModel>[] = playerIdsOnTeams.concat(extraPlayersToLoad.filter(id => !playerIdsOnTeams.includes(id)))
        .map(id => playerVersionsById[id].filter(item => chroniclerItemCoversTime(item, eventTime)))
        .reduce((a, b) => a.concat(b))
        .filter(data => eventText.includes(data.data.name))

    const playersBefore = playersBeforeData.map(item => item.data)

    const samePlayersAfter = playersBeforeData.map(playerDataBefore => {
        if (playerDataBefore.valid_to === null) {
            //console.warn(`No next value available for ${teamDataBefore.data.name} at ${eventTime} [${event.data.displayText}]`)
            return playerDataBefore;
        }
        const nextTime = dateAdd(playerDataBefore.valid_to, 1);
        const matches = playerVersionsById[playerDataBefore.data.id].filter(item => chroniclerItemCoversTime(item, nextTime))
        if (matches.length !== 1) {
            console.warn(`Got ${matches.length} matches for the next version of ${playerDataBefore.data.name} at ${eventTime} [${eventText}]`)
            return playerDataBefore;
        }

        return matches[0];

    }).map(item => item.data)


    const playerIdsBefore = teamsBefore.map(team => team.roster.filter(player => player.rosterSlots.some(slot => slot.location !== "SHADOWS")).map(player => player.id))
        .reduce((a, b) => a.concat(b))
    const newPlayerIds = teamsAfter.map(team => team.roster.filter(player => player.rosterSlots.some(slot => slot.location !== "SHADOWS")))
        .reduce((a, b) => a.concat(b))
        .filter(player => !playerIdsBefore.includes(player.id))
        .map(player => player.id);

    const newPlayers = newPlayerIds.map(id => {
        const versions = playerVersionsById[id]
        if (versions.some(item => chroniclerItemCoversTime(item, eventTime))) {
            return versions.filter(item => chroniclerItemCoversTime(item, eventTime))
        }
        function time(timestamp: string) {
            return new Date(timestamp).getTime()
        }
        versions.sort((a, b) => time(a.valid_from) - time(b.valid_from))
        return [versions[0]]
    }).reduce((a, b) => a.concat(b), [])
        .map(item => item.data)
        .filter(newPlayer => !samePlayersAfter.map(player => player.id).includes(newPlayer.id))


    const playersAfter: PlayerModel[] = samePlayersAfter.concat(newPlayers)


    return {
        teamsBefore,
        teamsAfter,
        playersBefore,
        playersAfter
    }
}

function gameEventContext(event: GameEvent, allGames: IAllGames, teamVersionsById: Record<TeamID, ChroniclerItem<TeamModel>[]>, playerVersionsById: Record<PlayerID, ChroniclerItem<PlayerModel>[]>): EventWithContext | false {

    const game = allGames.gamesById[event.game_id];
    if (!game) {
        console.warn(`Couldn't match game for event ${event.data.displayText}`)
        return false;
    }

    const relevantTeamIds = [game.awayTeam.id, game.homeTeam.id];

    const { playersAfter, playersBefore, teamsAfter, teamsBefore } = eventContext(event.timestamp, event.data.displayText, relevantTeamIds, teamVersionsById, playerVersionsById);

    if (false) {
        console.log(event.data.displayText)
        console.log(teamsBefore)
        console.log(teamsAfter)
        console.log(playersBefore)
        console.log(playersAfter)
    }
    return {
        _type: "GameEvent",
        event,
        teamsBefore,
        teamsAfter,
        playersBefore,
        playersAfter
    }
}

function electionEventContext(event: ElectionEvent, allTeams: TeamModel[], teamVersionsById: Record<TeamID, ChroniclerItem<TeamModel>[]>, playerNames: string[],
    playerIdByName: Record<string, PlayerID>, playerVersionsById: Record<PlayerID, ChroniclerItem<PlayerModel>[]>): EventWithContext | false {

    const relevantTeamIds = (event.winningTeam) ? [event.winningTeam.id] : [];
    const eventText = event.outcomeStrings.join(";")

    allTeams.filter(team => eventText.includes(team.name)).forEach(team => {
        if (!relevantTeamIds.includes(team.id)) {
            relevantTeamIds.push(team.id)
        }
    })

    const relevantPlayers = playerNames.filter(name => eventText.includes(name))
    const extraPlayersToLoad = relevantPlayers.map(name => playerIdByName[name] || false).filter((value): value is PlayerID => !!value)

    const oneHour = 1000 * 60 * 60;
    const { playersAfter, playersBefore, teamsAfter, teamsBefore } = eventContext(event.timestamp, eventText, relevantTeamIds, teamVersionsById, playerVersionsById, extraPlayersToLoad, oneHour);


    if (false) {

        console.log(event.name)
        console.log(teamsBefore)
        console.log(teamsAfter)
        console.log(playersBefore)
        console.log(playersAfter)
    }

    return {
        _type: "ElectionEvent",
        event,
        teamsBefore,
        teamsAfter,
        playersBefore,
        playersAfter
    };
}

export const Events = {
    searches,
    keywords,
    splitMessage,
    getEventType,
    gameEventContext,
    electionEventContext,
    isGameEvent,
    isElectionEvent,
    identifier
}